import React, {useContext} from "react";
import LoginPage from "../../../pages/authentication/login";
import {
  Avatar,
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  Chip,
  Button,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import AuthAPI from "../../../api/firebase/AuthAPI";
import UserProfileContext from "../../../context/userProfile/UserProfileContext";
import Loader from "../../loader/Loader";

const LoginStep = ({user = null, loading}) => {
  const isSignedIn = Boolean(user?.id || user?.uid);
  const authAPI = new AuthAPI();
  const userProfileContext = useContext(UserProfileContext);

  const handleLogout = async () => {
    try {
      await authAPI
        .signoutUser()
        .then(() => {
          userProfileContext.setState(null);
        })
        .catch((err) => {
          console.log("Error in SignOut:", err);
        });
    } catch (ex) {
      console.log("Error sign out:", ex);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box sx={{py: {xs: 1.5, sm: 2.5}}}>
      {isSignedIn && !loading && (
        <Container component="main" maxWidth="sm" sx={{px: {xs: 1, sm: 2}}}>
          <Paper
            elevation={0}
            sx={{
              p: {xs: 2.5, sm: 3},
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
              boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
            }}
          >
            {/* Top User Header */}
            <Stack
              direction={{xs: "column", sm: "row"}}
              spacing={2}
              alignItems={{xs: "flex-start", sm: "center"}}
              justifyContent="space-between"
              sx={{pb: 2.5, borderBottom: "1px solid #f1f5f9"}}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={user.photoURL}
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: "primary.main",
                    fontSize: "1.2rem",
                    fontWeight: 700,
                    boxShadow: "0 4px 12px rgba(47, 93, 126, 0.25)",
                  }}
                >
                  {getInitials(user.displayName)}
                </Avatar>

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: "#0f172a",
                        fontSize: {xs: "1.05rem", sm: "1.15rem"},
                      }}
                    >
                      {user.displayName || "Valued Customer"}
                    </Typography>
                    <Chip
                      icon={<CheckCircleRoundedIcon sx={{fontSize: "14px !important", color: "#166534 !important"}} />}
                      label="Signed In"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        bgcolor: "#dcfce7",
                        color: "#166534",
                        borderRadius: "6px",
                      }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{mt: 0.5}}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <EmailOutlinedIcon sx={{fontSize: 15, color: "text.secondary"}} />
                      <Typography variant="body2" sx={{color: "text.secondary", fontSize: "0.84rem"}}>
                        {user.email}
                      </Typography>
                    </Stack>

                    {user.mobile && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <PhoneOutlinedIcon sx={{fontSize: 15, color: "text.secondary"}} />
                        <Typography variant="body2" sx={{color: "text.secondary", fontSize: "0.84rem"}}>
                          {user.mobile}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              </Stack>

              <Button
                variant="outlined"
                size="small"
                onClick={handleLogout}
                startIcon={<SwapHorizRoundedIcon sx={{fontSize: 18}} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  borderRadius: "8px",
                  borderColor: "#cbd5e1",
                  color: "#475569",
                  alignSelf: {xs: "flex-end", sm: "center"},
                  "&:hover": {
                    borderColor: "#94a3b8",
                    bgcolor: "#f8fafc",
                  },
                }}
              >
                Switch Account
              </Button>
            </Stack>

            {/* Reassurance Callout Box */}
            <Box
              sx={{
                mt: 2.5,
                p: 2,
                borderRadius: "12px",
                bgcolor: "#f0fdf4",
                border: "1px solid #bbf7d0",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <CheckCircleRoundedIcon sx={{color: "#16a34a", fontSize: 20, mt: 0.2}} />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{fontWeight: 700, color: "#166534", fontSize: "0.86rem"}}
                >
                  Account Verified & Ready
                </Typography>
                <Typography
                  variant="body2"
                  sx={{color: "#15803d", fontSize: "0.8rem", mt: 0.25, lineHeight: 1.4}}
                >
                  Your order invoices and live tracking updates will be sent to{" "}
                  <strong>{user.email}</strong>. Click Proceed below to select your delivery address.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      )}

      {!isSignedIn && !loading && (
        <Box sx={{maxWidth: 480, mx: "auto"}}>
          <LoginPage redirect={false} showHeader={false} />
        </Box>
      )}

      {loading && <Loader />}
    </Box>
  );
};

export default LoginStep;
